/* CASH REPORT — SET UP.

   Tuan Nguyen via Max, 2026-10-08: "I already set up the initial List using for the portal. Please
   use it to develop the Module 'Set up' in the Portal." The nine lists of his workbook
   (01. Portal_Setup_Lists.xlsx), each a tab, read and edited the way Project Approval's tables are.

   NOTHING ON THIS SCREEN CHANGES A LIST DIRECTLY. Max ruled that a Set up change takes effect only
   once the Group CFO approves it, so every edit lands in ONE open batch of changes, shown over the
   approved rows — green for a new row, amber for a changed one (the old value on hover), struck
   through for one about to go. The band at the top says where the batch is and holds its two
   buttons: send it, or (for the Group CFO) decide on it.

   THE DATA CHECK IS TUAN'S OWN COLUMN, computed by the server on the state approval would produce.
   An error stops the batch being sent; a warning is something people decide about. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Pencil, Trash2, Plus, Upload, Send, Undo2, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import { DecisionDialog } from './DecisionDialog';
import { BACKEND_URL } from './backendConfig';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { fmtStamp, fmtDay, call, TestAsBar } from './cashUi';
import './SPHub.css';
import './ProjectApproval.css';
import './CashSetup.css';

const API = `${BACKEND_URL}/api/cash`;
/* cashUi's `call` (2026-10-16), so Set up carries the Test as role like the report screens: the
   Group CFO persona decides a Set up change, every other persona reads. */
// Dates read 08/Oct/2026 14:05 — a month name, as on the report screens (cashUi).
const stamp = fmtStamp;
const dash = <span className="pa-muted">—</span>;
const yesNo = (v, t) => (v ? t('cash.yes') : t('cash.no'));

/* ---- the columns of each list, in Tuan's order -----------------------------------------------
   `key`: the row's code. `auto`: the portal issues it. `lock`: fixed once the row exists (the code
   was issued from it). `ref`: a dropdown fed by another list. */
const LIST_TABS = ['entity', 'account', 'user', 'period', 'fx', 'expense', 'account_type', 'role', 'notice'];
const COLS = {
  entity: [
    { f: 'code', key: true, w: 90 }, { f: 'name', w: 200 }, { f: 'tax_code', w: 130 },
    { f: 'legal_address', wrap: true, w: 230 }, { f: 'country', w: 110 }, { f: 'ho_address', wrap: true, w: 230 },
    { f: 'acc_mail', w: 190 }, { f: 'min_balance_sgd', type: 'decimal', w: 120 },
    { f: 'status', type: 'enum', opts: ['Active', 'Inactive'], w: 90 },
  ],
  account: [
    { f: 'account_id', key: true, auto: true, w: 100 }, { f: 'entity_code', type: 'ref', ref: 'entity', lock: true, w: 90 },
    { f: 'account_no', w: 190 }, { f: 'currency', type: 'ref', ref: 'currency', w: 80 }, { f: 'bank_name', w: 150 },
    { f: 'account_type', type: 'ref', ref: 'account_type', w: 160 },
    { f: 'restricted', type: 'bool', w: 90 }, { f: 'fixed_deposit', type: 'bool', w: 100 },
    { f: 'status', type: 'enum', opts: ['Active', 'Closing', 'Closed'], w: 90 }, { f: 'remarks', wrap: true, w: 180 },
  ],
  user: [
    { f: 'user_code', key: true, auto: true, w: 100 }, { f: 'name', w: 160 }, { f: 'mail', w: 230 },
    { f: 'entity_code', type: 'ref', ref: 'entityAll', lock: true, w: 90 },
    { f: 'role_key', type: 'enum', opts: 'roles', w: 120 }, { f: 'status', type: 'enum', opts: ['Active', 'Inactive'], w: 90 },
  ],
  period: [
    { f: 'code', key: true, auto: true, w: 90 }, { f: 'year', type: 'int', lock: true, w: 70 }, { f: 'week', type: 'int', lock: true, w: 70 },
    { f: 'week_ending', ro: true, w: 140 }, { f: 'submit_deadline', ro: true, w: 140 },
    { f: 'status', type: 'enum', opts: ['Open', 'Closed'], w: 90 },
  ],
  expense: [
    { f: 'code', key: true, auto: true, w: 100 }, { f: 'name', w: 260 }, { f: 'category_type', w: 170 },
    { f: 'describe', wrap: true, w: 260 }, { f: 'status', type: 'enum', opts: ['Active', 'Inactive'], w: 90 },
  ],
  account_type: [
    { f: 'type_name', key: true, w: 190 }, { f: 'describe', wrap: true, w: 380 },
    { f: 'default_restricted', type: 'bool', w: 150 }, { f: 'default_fd', type: 'bool', w: 160 },
  ],
  role: [
    { f: 'role_key', key: true, w: 120 }, { f: 'action', wrap: true, w: 260 },
    { f: 'right_scope', type: 'enum', opts: ['Entity', 'All', 'Set up only', 'None'], w: 110 }, { f: 'restriction', wrap: true, w: 360 },
  ],
  notice: [
    { f: 'notice_id', key: true, auto: true, w: 80 }, { f: 'header', w: 180 }, { f: 'content', wrap: true, w: 360, area: true },
    { f: 'report', type: 'enum', opts: 'reports', w: 160 }, { f: 'send_to_role', type: 'enum', opts: 'roles', w: 120 },
    { f: 'send_day', type: 'enum', opts: 'days', w: 90 }, { f: 'send_time', w: 80 },
    { f: 'status', type: 'enum', opts: ['Active', 'Inactive'], w: 90 },
    /* What sets the notice off — the portal's column, not Tuan's: his list says what is sent, to
       whom and when, and this says which event. Printed in words, stored as the key. */
    { f: 'trigger_key', type: 'enum', opts: 'triggers', w: 190, label: (x, t) => t(`cash.trg_${x}`) },
  ],
};
const FIXED = new Set(['role']);   // the six roles are part of the logic: edited, never added or removed

const shown = (c, v, t) => {
  if (v === null || v === undefined || v === '') return dash;
  if (c.type === 'bool') return yesNo(v, t);
  if (c.type === 'decimal') return Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (c.label) return c.label(v, t);
  return String(v);
};

/* ---- one list as a table ------------------------------------------------------------------- */
function ListTable({ t, list, rows, data, me, locked, busy, onSave, onDelete, onUndo, onlyIssues, onlyChanged }) {
  const cols = COLS[list];
  const keyCol = cols.find((c) => c.key);
  const [edit, setEdit] = useState(null);     // { key, draft } — key '__new__' for an added row
  const [search, setSearch] = useState('');
  const may = me.canEdit && !locked;

  const opts = (c) => {
    if (c.type === 'ref') {
      if (c.ref === 'entityAll') return ['ALL', ...data.entity.filter((r) => r._op !== 'delete').map((r) => r.code)];
      if (c.ref === 'entity') return data.entity.filter((r) => r._op !== 'delete').map((r) => r.code);
      if (c.ref === 'currency') return data.currency.filter((r) => r._op !== 'delete').map((r) => r.code);
      if (c.ref === 'account_type') return data.account_type.filter((r) => r._op !== 'delete').map((r) => r.type_name);
    }
    if (c.opts === 'roles') return data.__options.roles;
    if (c.opts === 'reports') return data.__options.reports;
    if (c.opts === 'days') return data.__options.days;
    if (c.opts === 'triggers') return data.__options.triggers || [];
    return c.opts || [];
  };

  const q = search.trim().toLowerCase();
  const pre = rows.filter((r) => (!onlyIssues || r._checks.length) && (!onlyChanged || r._op)
    && (!q || cols.some((c) => String(r[c.f] ?? '').toLowerCase().includes(q))));
  const columns = cols.map((c) => ({ key: c.f, get: (r) => (c.type === 'bool' ? yesNo(r[c.f], t) : r[c.f] ?? '') }))
    .concat([{ key: '_check', get: (r) => (r._checks.some((x) => x.level === 'error') ? t('cash.chkError')
      : r._checks.length ? t('cash.chkWarn') : t('cash.chkOk')) }]);
  const tools = useColumnTools(pre, columns);

  const startAdd = () => {
    const draft = {};
    for (const c of cols) draft[c.f] = c.type === 'bool' ? false : (c.type === 'enum' && Array.isArray(c.opts) ? c.opts[0] : '');
    if (list === 'user') draft.role_key = 'Preparer';
    if (list === 'notice') { draft.report = data.__options.reports[0]; draft.send_to_role = 'Preparer'; draft.send_day = 'Mon'; }
    if (list === 'period') draft.year = new Date().getFullYear();
    setEdit({ key: '__new__', draft });
  };
  const save = async () => {
    const ok = await onSave(list, edit.key === '__new__' ? 'insert' : 'update', edit.key === '__new__' ? null : edit.key, edit.draft);
    if (ok) setEdit(null);
  };

  /* A row that is only a PENDING insert has no history yet, so its entity or typed code may still be
     corrected — the server re-issues its code. An approved row keeps both. */
  const input = (c, isNew) => {
    const v = edit.draft[c.f];
    const set = (val) => setEdit((e) => ({ ...e, draft: { ...e.draft, [c.f]: val } }));
    const open = isNew || edit.draft._op === 'insert';
    if (c.ro || (c.key && (c.auto || !open)) || (c.lock && !open)) {
      return <span className="cs-ro">{c.auto && isNew ? <i className="pa-muted">{t('cash.auto')}</i> : shown(c, v, t)}</span>;
    }
    if (c.type === 'bool') return <input type="checkbox" checked={!!v} onChange={(e) => set(e.target.checked)} />;
    if (c.type === 'enum' || c.type === 'ref') {
      const o = opts(c);
      return (
        <select value={v ?? ''} onChange={(e) => set(e.target.value)}>
          {(c.type === 'ref' || !o.includes(v)) && <option value="">{t('cash.choose')}</option>}
          {o.map((x) => <option key={x} value={x}>{c.label ? c.label(x, t) : x}</option>)}
        </select>
      );
    }
    if (c.area) return <textarea rows={3} value={v ?? ''} onChange={(e) => set(e.target.value)} />;
    return (
      <input type={c.type === 'int' || c.type === 'decimal' ? 'number' : 'text'} value={v ?? ''}
        step={c.type === 'decimal' ? 'any' : undefined} onChange={(e) => set(e.target.value)} />
    );
  };

  const rowClass = (r) => `cs-row${r._op ? ` cs-${r._op}` : ''}`;
  const changedField = (r, f) => r._op === 'update' && r._before && String(r._before[f] ?? '') !== String(r[f] ?? '');

  const editRow = (isNew, r) => (
    <tr className={`cs-row cs-editing${isNew ? ' cs-insert' : ''}`} key={isNew ? '__new__' : r._key}>
      {cols.map((c) => <td key={c.f} className={c.wrap ? 'cs-wrap' : ''}>{input(c, isNew)}</td>)}
      <td />
      <td className="cs-acts">
        <button type="button" className="pa-btn approve cs-icon" disabled={busy} onClick={save} title={t('cash.save')}><Check size={14} /></button>
        <button type="button" className="pa-btn cs-icon" disabled={busy} onClick={() => setEdit(null)} title={t('cash.cancel')}><X size={14} /></button>
      </td>
    </tr>
  );

  return (
    <div className="pa-section">
      <div className="pa-toolbar">
        <span className="pa-count-text">{t('cash.rows', { count: tools.shown.length })}</span>
        <input className="cs-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('cash.search')} />
        {(search || tools.active) && (
          <button type="button" className="pa-btn" onClick={() => { setSearch(''); tools.clear(); }}>{t('cash.clear')}</button>
        )}
        <span style={{ flex: 1 }} />
        {may && !FIXED.has(list) && !edit && (
          <button type="button" className="pa-btn primary" onClick={startAdd}><Plus size={14} /> {t('cash.addRow')}</button>
        )}
      </div>
      <div className="pa-table-wrap cs-table-wrap cs-freeze">
        <table className="pa-table cs-table">
          <colgroup>
            {cols.map((c) => <col key={c.f} style={{ width: c.w }} />)}
            <col style={{ width: 220 }} /><col style={{ width: 92 }} />
          </colgroup>
          <thead>
            <tr>
              {cols.map((c) => <SortTh key={c.f} label={t(`cash.f_${c.f}`)} {...tools.thProps(c.f)} />)}
              <SortTh label={t('cash.dataCheck')} {...tools.thProps('_check')} />
              <th aria-label={t('xCash.aria.actions')} />
            </tr>
          </thead>
          <tbody>
            {edit?.key === '__new__' && editRow(true)}
            {tools.shown.map((r) => (edit && edit.key === r._key ? editRow(false, r) : (
              <tr key={r._key} className={rowClass(r)}>
                {cols.map((c) => (
                  <td key={c.f} className={`${c.wrap ? 'cs-wrap' : ''}${c.key ? ' mono' : ''}${changedField(r, c.f) ? ' cs-changed' : ''}`}
                    title={changedField(r, c.f) ? `${t('cash.was')}: ${r._before[c.f] ?? '—'}` : undefined}>
                    {shown(c, r[c.f], t)}
                  </td>
                ))}
                <td className="cs-check">
                  {r._checks.length === 0 ? <span className="cs-ok">{t('cash.chkOk')}</span> : r._checks.map((x, i) => (
                    <div key={i} className={`cs-msg ${x.level}`}>{x.msg}</div>
                  ))}
                </td>
                <td className="cs-acts">
                  {r._op && <span className={`cs-op ${r._op}`}>{t(`cash.op_${r._op}`)}</span>}
                  {may && !edit && r._op !== 'delete' && (
                    <button type="button" className="pa-btn cs-icon" title={t('cash.edit')} disabled={busy}
                      onClick={() => setEdit({ key: r._key, draft: { ...r } })}><Pencil size={13} /></button>
                  )}
                  {may && !edit && !FIXED.has(list) && !r._op && (
                    <button type="button" className="pa-btn reject cs-icon" title={t('cash.remove')} disabled={busy}
                      onClick={() => onDelete(list, r._key)}><Trash2 size={13} /></button>
                  )}
                  {may && !edit && r._op && (
                    <button type="button" className="pa-btn cs-icon" title={t('cash.undo')} disabled={busy}
                      onClick={() => onUndo(r._change)}><Undo2 size={13} /></button>
                  )}
                </td>
              </tr>
            )))}
          </tbody>
        </table>
        {tools.shown.length === 0 && edit?.key !== '__new__' && <div className="pa-empty">{t('cash.noRows')}</div>}
      </div>
      {keyCol?.auto && may && <p className="cs-hint">{t('cash.autoHint')}</p>}
    </div>
  );
}

/* ---- exchange rates: weeks down, currencies across ----------------------------------------- */
function FxGrid({ t, data, me, locked, busy, onSaveMany, onSave, onSync }) {
  const years = [...new Set(data.period.map((p) => p.year))].sort();
  const [year, setYear] = useState(() => (years.includes(new Date().getFullYear()) ? new Date().getFullYear() : years[0]));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [newCur, setNewCur] = useState('');
  const curs = data.currency.filter((c) => c._op !== 'delete' && c.status === 'Active').map((c) => c.code);
  /* A WEEK PICKER beside the year (Tuan via Max, 2026-10-13: "Can the Exchange Rate table display the
     Week Number with a dropdown for selection (in addition to the Year)?"). Empty = every week of the
     year; a week = that week's row alone. A year change resets it, since a week belongs to one year. */
  const [weekPick, setWeekPick] = useState('');
  const yearWeeks = data.period.filter((p) => p.year === year && p._op !== 'delete')
    .sort((a, b) => String(a.week_ending).localeCompare(String(b.week_ending)));
  const weeks = weekPick ? yearWeeks.filter((p) => p.code === weekPick) : yearWeeks;
  const rate = new Map(data.fx.filter((f) => f._op !== 'delete').map((f) => [`${f.week_code}|${f.currency}`, f]));
  const setting = (k) => data.setting.find((s) => s.setting_key === k);
  const may = me.canEdit && !locked;
  const [sdraft, setSdraft] = useState(null);
  /* When the NetSuite rates last arrived (Max, 2026-10-13: the Friday's rate, no Group CFO approval).
     Read off the rows themselves, so it is the time of the last rate that actually CHANGED. */
  const lastSync = data.fx.filter((f) => f._source === 'NetSuite' && f._updated_at)
    .reduce((m, f) => (!m || f._updated_at > m ? f._updated_at : m), null);
  /* EXCEL'S SORT AND FILTER ON EVERY COLUMN, as the other Set up lists have (Max, 2026-10-13: "bảng
     thiếu sort và filter như excel nhé"). The rows are the year's weeks; a currency column sorts and
     filters on the rate the cell shows — a NUMBER, so 1.3 does not sort before 1.28 — and the Week
     Ending column takes the Year > Month > Day tree. */
  const columns = [
    { key: 'code', get: (w) => w.code },
    { key: 'week_ending', get: (w) => w.week_ending },
    ...curs.map((c) => ({ key: `fx_${c}`, get: (w) => { const r = rate.get(`${w.code}|${c}`); return r ? Number(r.rate) : ''; } })),
  ];
  const tools = useColumnTools(weeks, columns);
  const cellTitle = (cur) => {
    if (!cur || cur._op) return undefined;
    if (cur._source === 'NetSuite') return t('cash.fxFromNetsuite', { day: cur._source_date || '' });
    if (cur._source === 'Base') return t('cash.fxBase');
    return t('cash.fxTyped');
  };

  const save = async () => {
    const ops = [];
    for (const [k, v] of Object.entries(draft)) {
      const cur = rate.get(k);
      const [week_code, currency] = k.split('|');
      const val = String(v).trim();
      if (!val && cur) ops.push({ list: 'fx', op: 'delete', key: k });
      else if (val && !cur) ops.push({ list: 'fx', op: 'insert', data: { week_code, currency, rate: val } });
      else if (val && cur && String(cur.rate) !== val) ops.push({ list: 'fx', op: 'update', key: k, data: { rate: val } });
    }
    if (await onSaveMany(ops)) { setEditing(false); setDraft({}); }
  };
  const saveSettings = async () => {
    const ops = [];
    for (const k of data.__options.settings) {
      const cur = setting(k);
      const v = String(sdraft[k] ?? '').trim();
      if (cur && String(cur.setting_value ?? '') !== v) ops.push({ list: 'setting', op: 'update', key: k, data: { setting_value: v } });
      if (!cur && v) ops.push({ list: 'setting', op: 'insert', data: { setting_key: k, setting_value: v } });
    }
    if (await onSaveMany(ops)) setSdraft(null);
  };

  return (
    <div className="pa-section">
      <div className="cs-fx-settings">
        {data.__options.settings.map((k) => (
          <label key={k}>
            <span>{t(`cash.s_${k.replace('fx.', '')}`)}</span>
            {sdraft ? <input value={sdraft[k] ?? ''} onChange={(e) => setSdraft((d) => ({ ...d, [k]: e.target.value }))} />
              : <b className={setting(k)?._op ? 'cs-changed' : ''}>{setting(k)?.setting_value || dash}</b>}
          </label>
        ))}
        {may && !sdraft && (
          <button type="button" className="pa-btn" onClick={() => setSdraft(Object.fromEntries(data.__options.settings.map((k) => [k, setting(k)?.setting_value || ''])))}>
            <Pencil size={13} /> {t('cash.edit')}
          </button>
        )}
        {sdraft && (
          <>
            <button type="button" className="pa-btn approve" disabled={busy} onClick={saveSettings}><Check size={13} /> {t('cash.save')}</button>
            <button type="button" className="pa-btn" onClick={() => setSdraft(null)}>{t('cash.cancel')}</button>
          </>
        )}
      </div>
      <p className="cs-hint">{t('cash.fxHint')}</p>
      <div className="pa-toolbar">
        <span className="pa-count-text">{t('cash.year')}</span>
        <select value={year || ''} onChange={(e) => { setYear(Number(e.target.value)); setWeekPick(''); setDraft({}); }}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="pa-count-text">{t('cash.week')}</span>
        <select className="cs-fx-week" value={weekPick} onChange={(e) => setWeekPick(e.target.value)} aria-label={t('cash.week')}>
          <option value="">{t('cash.allWeeks')}</option>
          {yearWeeks.map((w) => <option key={w.code} value={w.code}>{w.code} · {fmtDay(w.week_ending)}</option>)}
        </select>
        <span className="pa-count-text">{t('cash.rows', { count: tools.shown.length })}</span>
        {(tools.active || weekPick) && (
          <button type="button" className="pa-btn" onClick={() => { tools.clear(); setWeekPick(''); }}>{t('cash.clear')}</button>
        )}
        <span className="pa-count-text cs-fx-last">
          {lastSync ? t('cash.fxLastSync', { when: stamp(lastSync) }) : t('cash.fxNeverSynced')}
        </span>
        <span style={{ flex: 1 }} />
        {/* Not gated on the open batch: these rates do not go through it, so a batch sitting with the
            Group CFO is no reason to keep last Friday's rates out. */}
        {me.canEdit && !editing && (
          <button type="button" className="pa-btn cs-fx-sync" disabled={busy} onClick={onSync} title={t('cash.fxSyncTitle')}>
            <RefreshCw size={13} /> {t('cash.fxSync')}
          </button>
        )}
        {may && !editing && (
          <>
            <input className="cs-newcur" value={newCur} maxLength={3} placeholder={t('cash.newCurrency')}
              onChange={(e) => setNewCur(e.target.value.toUpperCase())} />
            <button type="button" className="pa-btn" disabled={busy || !/^[A-Z]{3}$/.test(newCur)}
              onClick={async () => { if (await onSave('currency', 'insert', null, { code: newCur, seq: curs.length + 1 })) setNewCur(''); }}>
              <Plus size={13} /> {t('cash.addCurrency')}
            </button>
            <button type="button" className="pa-btn primary" onClick={() => setEditing(true)}><Pencil size={13} /> {t('cash.editRates')}</button>
          </>
        )}
        {editing && (
          <>
            <button type="button" className="pa-btn approve" disabled={busy} onClick={save}><Check size={13} /> {t('cash.saveRates', { count: Object.keys(draft).length })}</button>
            <button type="button" className="pa-btn" onClick={() => { setEditing(false); setDraft({}); }}>{t('cash.cancel')}</button>
          </>
        )}
      </div>
      <div className="pa-table-wrap cs-table-wrap cs-freeze">
        <table className="pa-table cs-table cs-fx">
          <thead>
            <tr>
              <SortTh label={t('cash.f_code')} {...tools.thProps('code')} />
              <SortTh label={t('cash.f_week_ending')} {...tools.thProps('week_ending')} />
              {curs.map((c) => (
                <SortTh key={c} className="num" label={`${c}/${setting('fx.base_currency')?.setting_value || 'SGD'}`} {...tools.thProps(`fx_${c}`)} />
              ))}
            </tr>
          </thead>
          <tbody>
            {tools.shown.map((w) => (
              <tr key={w.code}>
                <td className="mono">{w.code}</td>
                <td>{w.week_ending}</td>
                {curs.map((c) => {
                  const k = `${w.code}|${c}`;
                  const cur = rate.get(k);
                  return (
                    <td key={c} title={cellTitle(cur)}
                        className={`num${cur?._op ? ` cs-${cur._op === 'insert' ? 'ins' : 'chg'}` : ''}${cur && !cur._op && cur._source == null ? ' cs-fx-typed' : ''}`}>
                      {editing
                        ? <input className="cs-rate" type="number" step="any" value={draft[k] ?? (cur ? cur.rate : '')}
                            onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
                        : (cur ? <>{cur.rate}{!cur._op && cur._source == null && <sup className="cs-fx-mark">✎</sup>}</> : dash)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- the flow, as Tuan's Set up sheet writes it -------------------------------------------- */
function FlowTab({ t }) {
  /* Two steps for the three reports since 2026-10-16 (Max: "CFO và CEO thì ko tham gia vào quá trình
     duyệt nhé") — the Reviewer's agreement is the last one. Set up itself still goes to the Group CFO. */
  const two = [t('cash.flowPrep'), t('cash.flowReview')];
  const rows = [
    [t('cash.rep_weekly'), ...two],
    [t('cash.rep_13w'), ...two],
    [t('cash.rep_month'), ...two],
    [t('cash.rep_setup'), t('cash.flowAdmin'), t('cash.flowCfo')],
  ];
  return (
    <div className="pa-section">
      <table className="pa-table cs-table cs-flow">
        <thead><tr><th>{t('cash.flowReport')}</th><th>{t('cash.step', { n: 1 })}</th><th>{t('cash.step', { n: 2 })}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]}>{r.map((c, i) => <td key={i} className={i === 0 ? 'cs-flow-name' : ''}>{c || dash}</td>)}</tr>
          ))}
        </tbody>
      </table>
      <p className="cs-hint">{t('cash.flowNote')}</p>
    </div>
  );
}

/* ---- the batch's changes, one line each --------------------------------------------------- */
function ChangesTab({ t, batch, me, busy, onUndo }) {
  if (!batch || !batch.changes.length) return <div className="pa-section"><div className="pa-empty">{t('cash.noChanges')}</div></div>;
  const diff = (c) => {
    if (c.op !== 'update' || !c.before) return null;
    return Object.keys(c.data || {}).filter((f) => String(c.before[f] ?? '') !== String(c.data[f] ?? ''))
      .map((f) => `${t(`cash.f_${f}`)}: ${c.before[f] ?? '—'} → ${c.data[f] ?? '—'}`).join(' · ');
  };
  return (
    <div className="pa-section">
      <div className="pa-table-wrap cs-table-wrap cs-freeze">
        <table className="pa-table cs-table">
          <thead><tr><th>{t('cash.list')}</th><th>{t('cash.code')}</th><th>{t('cash.change')}</th><th>{t('cash.what')}</th><th>{t('cash.by')}</th><th aria-label={t('xCash.aria.actions')} /></tr></thead>
          <tbody>
            {batch.changes.map((c) => (
              <tr key={c.change_pk} className={`cs-row cs-${c.op}`}>
                <td>{t(`cash.tab_${c.list_key}`)}</td>
                <td className="mono">{c.row_key}</td>
                <td><span className={`cs-op ${c.op}`}>{t(`cash.op_${c.op}`)}</span></td>
                <td className="cs-wrap">{diff(c) || (c.op === 'insert' ? Object.entries(c.data || {}).filter(([, v]) => v !== null && v !== '' && v !== false).slice(0, 4).map(([, v]) => v).join(' · ') : '')}</td>
                <td>{c.updated_by || c.created_by}<div className="pa-muted">{stamp(c.updated_at || c.created_at)}</div></td>
                <td className="cs-acts">
                  {me.canEdit && batch.status === 'draft' && (
                    <button type="button" className="pa-btn cs-icon" title={t('cash.undo')} disabled={busy} onClick={() => onUndo(c.change_pk)}><Undo2 size={13} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- every approved batch, and what it changed --------------------------------------------- */
/* ---- the notice log --------------------------------------------------------------------------
   Every Cash mail the portal has built — a step in a report's flow or a scheduled notice — held or
   sent. While `cash.mail_live` is off this is the only place they can be read, so it is also how
   anybody checks what Monday 09:00 WOULD send: Run as at a moment, read the rows, nothing leaves. */
function NoticeLog({ t }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [at, setAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(null);
  const load = useCallback(() => call(`${API}/notices/log`).then((j) => { setD(j); setErr(''); }).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    setBusy(true); setMsg('');
    try {
      const j = await call(`${API}/notices/run`, { method: 'POST', body: JSON.stringify(at ? { at } : {}) });
      setMsg(t('cash.logRan', { at: j.at, count: j.sent.length }));
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  if (err) return <div className="pa-section"><div className="pa-banner err">{err}</div></div>;
  if (!d) return <div className="pa-section"><div className="pa-empty"><Loader2 size={16} className="pa-spin" /></div></div>;
  return (
    <div className="pa-section">
      <div className={`cs-band ${d.mailLive ? 'clean' : 'submitted'}`}>
        <span className={`cs-state ${d.mailLive ? 'clean' : 'submitted'}`}>{d.mailLive ? t('cash.mailLive') : t('cash.mailHeld')}</span>
        <div className="cs-band-text">{d.mailLive ? t('cash.mailLiveLine') : t('cash.mailHeldLine')}</div>
        {d.canRun && (
          <div className="cs-band-acts">
            {!d.mailLive && <input type="datetime-local" className="cs-note cs-at" value={at} onChange={(e) => setAt(e.target.value)} title={t('cash.logAtWhy')} />}
            <button type="button" className="pa-btn" disabled={busy} onClick={runNow}>{at ? t('cash.logRunAt') : t('cash.logRun')}</button>
          </div>
        )}
      </div>
      {msg && <div className="pa-banner ok">{msg}</div>}
      {!d.rows.length ? <div className="pa-empty">{t('cash.logEmpty')}</div> : (
        <div className="cs-table-wrap cs-freeze">
          <table className="pa-table cs-table cs-log">
            <thead><tr>
              <th style={{ width: 130 }}>{t('cash.logWhen')}</th><th style={{ width: 70 }}>{t('cash.logStatus')}</th>
              <th style={{ width: 170 }}>{t('cash.logWhat')}</th><th style={{ width: 120 }}>{t('cash.logFor')}</th>
              <th style={{ width: 230 }}>{t('cash.logTo')}</th><th>{t('cash.logSubject')}</th>
            </tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <React.Fragment key={r.log_pk}>
                  <tr className="cs-row" onClick={() => setOpen(open === r.log_pk ? null : r.log_pk)} style={{ cursor: 'pointer' }}>
                    <td>{stamp(r.created_at)}</td>
                    <td>
                      <span className={`cs-op ${r.status === 'held' ? 'update' : ['sent', 'tester'].includes(r.status) ? 'insert' : 'delete'}`}>{t(`cash.log_${r.status}`)}</span>
                      {String(r.run_key || '').startsWith('SIM|') && <div className="pa-muted" title={t('cash.logSimWhy')}>{t('cash.logSim')}</div>}
                    </td>
                    <td>{r.notice_id ? <b className="mono">{r.notice_id}</b> : null} {r.trigger_key ? t(`cash.trg_${r.trigger_key}`) : ''}</td>
                    <td>{[r.entity_code, r.period_code].filter(Boolean).join(' · ')}</td>
                    <td className="cs-wrap">{r.mail_to}{r.mail_cc ? <div className="pa-muted">cc {r.mail_cc}</div> : null}</td>
                    <td className="cs-wrap">{r.subject}</td>
                  </tr>
                  {open === r.log_pk && <tr><td colSpan={6} className="cs-wrap"><pre className="cs-logbody">{r.body}</pre></td></tr>}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HistoryTab({ t }) {
  const [batches, setBatches] = useState(null);
  const [open, setOpen] = useState(null);
  const [changes, setChanges] = useState([]);
  useEffect(() => { call(`${API}/setup/history`).then((j) => setBatches(j.batches)).catch(() => setBatches([])); }, []);
  const toggle = async (b) => {
    if (open === b.batch_pk) { setOpen(null); return; }
    setOpen(b.batch_pk);
    setChanges((await call(`${API}/setup/history?batch=${b.batch_pk}`)).changes);
  };
  if (!batches) return <div className="pa-section"><div className="pa-empty"><Loader2 size={16} className="pa-spin" /></div></div>;
  if (!batches.length) return <div className="pa-section"><div className="pa-empty">{t('cash.noHistory')}</div></div>;
  return (
    <div className="pa-section">
      {batches.map((b) => (
        <div key={b.batch_pk} className="cs-hist">
          <button type="button" className="cs-hist-head" onClick={() => toggle(b)}>
            {open === b.batch_pk ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            <b className="mono">{b.batch_no}</b>
            {/* A batch applied for the BUILD (2026-10-08) says so — it was never the Group CFO's. */}
            <span>{b.status === 'build'
              ? t('cash.histBuild', { n: b.n, by: b.decided_by, on: stamp(b.decided_at) })
              : t('cash.histLine', { n: b.n, by: b.submitted_by || b.created_by, at: stamp(b.submitted_at), cfo: b.decided_by, on: stamp(b.decided_at) })}</span>
          </button>
          {b.decision_note && <div className="cs-hist-note">{b.decision_note}</div>}
          {open === b.batch_pk && (
            <table className="pa-table cs-table">
              <tbody>
                {changes.map((c) => (
                  <tr key={c.change_pk}>
                    <td style={{ width: 150 }}>{t(`cash.tab_${c.list_key}`)}</td>
                    <td className="mono" style={{ width: 140 }}>{c.row_key}</td>
                    <td style={{ width: 90 }}><span className={`cs-op ${c.op}`}>{t(`cash.op_${c.op}`)}</span></td>
                    <td className="cs-wrap">{c.op === 'update' && c.before
                      ? Object.keys(c.data || {}).filter((f) => String(c.before[f] ?? '') !== String(c.data[f] ?? ''))
                        .map((f) => `${t(`cash.f_${f}`)}: ${c.before[f] ?? '—'} → ${c.data[f] ?? '—'}`).join(' · ')
                      : ''}</td>
                    <td style={{ width: 160 }}>{c.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---- the screen ----------------------------------------------------------------------------- */
/* THE GUIDE (Max 01/11: every module has one, ending on the manual — the roles manual, which says who prepares, reviews and
   approves a change here). It walks the first tab (Entity), so it first switches back to it. Import, Add and the
   Send / Approve button are drawn only for a person who may edit or approve, and only when the batch allows it; a step
   whose control is missing is skipped. */
const TOUR = [
  { selector: '.cs-band', title: 'tourCashSetup.bandT', body: 'tourCashSetup.bandB' },
  { selector: '.cs-checkbar', title: 'tourCashSetup.checksT', body: 'tourCashSetup.checksB' },
  { selector: '.pa-tabs', title: 'tourCashSetup.tabsT', body: 'tourCashSetup.tabsB' },
  { selector: '.cs-band-acts label.pa-btn', title: 'tourCashSetup.importT', body: 'tourCashSetup.importB' },
  { selector: '.cs-page .pa-section .pa-toolbar .pa-btn.primary', title: 'tourCashSetup.addT', body: 'tourCashSetup.addB' },
  { selector: '.cs-band-acts .pa-btn.submit, .cs-band-acts .pa-btn.approve', title: 'tourCashSetup.decideT', body: 'tourCashSetup.decideB' },
];

export default function CashSetup() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('entity');
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [sendNote, setSendNote] = useState('');
  const [deciding, setDeciding] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [newYear, setNewYear] = useState(new Date().getFullYear() + 1);

  const say = (text, bad = false, extra) => setMsg({ text, bad, extra });
  const load = useCallback(async () => {
    try {
      const j = await call(`${API}/setup`);
      setData({ ...j.lists, __options: j.options, __batch: j.batch, __me: j.me, __empty: j.approvedEmpty });
      setError('');
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      if (okText) say(okText);
      await load();
      return true;
    } catch (e) {
      say(e.message, true, e.blocking);
      return false;
    } finally { setBusy(false); }
  };
  const saveRow = (list, op, key, d) => run(() => call(`${API}/setup/change`, { method: 'POST', body: JSON.stringify({ list, op, key, data: d }) }));
  const saveMany = (ops) => run(async () => {
    for (const o of ops) {
      // eslint-disable-next-line no-await-in-loop
      await call(`${API}/setup/change`, { method: 'POST', body: JSON.stringify(o) });
    }
  });
  const delRow = (list, key) => run(() => call(`${API}/setup/change`, { method: 'POST', body: JSON.stringify({ list, op: 'delete', key }) }));
  /* The rates from NetSuite: written straight into the approved list, never into the batch. What came
     back is said in full — a typed rate it left alone and a week NetSuite had nothing for are both
     things somebody may need to act on. */
  const syncFx = () => run(async () => {
    const j = await call(`${API}/setup/fx/sync`, { method: 'POST' });
    const parts = [t('cash.fxSynced', { written: j.written, unchanged: j.unchanged, weeks: j.weeks })];
    if (j.keptTyped.length) parts.push(t('cash.fxKeptTyped', { n: j.keptTyped.length, list: j.keptTyped.slice(0, 6).join(', ') }));
    if (j.missing.length) parts.push(t('cash.fxMissing', { n: j.missing.length, list: j.missing.slice(0, 6).join(', ') }));
    say(parts.join(' '), !!j.missing.length);
  });
  const undo = (id) => run(() => call(`${API}/setup/change/${id}`, { method: 'DELETE' }));

  const onImport = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = String(reader.result).split(',')[1];
      setBusy(true);
      try {
        const j = await call(`${API}/setup/import`, { method: 'POST', body: JSON.stringify({ fileBase64: b64, fileName: file.name }) });
        const n = Object.values(j.summary).reduce((a, s) => ({ insert: a.insert + s.insert, update: a.update + s.update, same: a.same + s.same }), { insert: 0, update: 0, same: 0 });
        say(t('cash.imported', n), false, j.problems?.length ? j.problems : undefined);
        await load();
      } catch (e) { say(e.message, true); } finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const counts = useMemo(() => {
    if (!data) return { error: 0, warn: 0 };
    let error = 0; let warn = 0;
    for (const l of LIST_TABS.filter((x) => x !== 'fx')) {
      for (const r of data[l] || []) {
        if (r._checks.some((x) => x.level === 'error')) error += 1;
        else if (r._checks.length) warn += 1;
      }
    }
    return { error, warn };
  }, [data]);

  if (error) {
    return (
      <div className="sp-page"><Header /><div className="sp-main">
        <div className="pa-title"><h2>{t('cash.title')}</h2></div>
        <div className="pa-banner err">{error}</div>
      </div></div>
    );
  }
  if (!data) {
    return <div className="sp-page"><Header /><div className="sp-main"><div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('cash.loading')}</div></div></div>;
  }
  const me = data.__me;
  const batch = data.__batch;
  const locked = batch?.status === 'submitted';
  const tabCount = (l) => (l === 'fx' ? data.fx.length : (data[l] || []).length);
  const issueCount = (l) => (data[l] || []).filter((r) => r._checks.some((x) => x.level === 'error')).length;

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main cs-page">
        <div className="mh-titlerow">
          <div className="pa-title">
            <h2>{t('cash.title')}</h2>
            <p>{t('cash.sub')}</p>
          </div>
          <ModuleHelp manual="cash-roles" steps={TOUR} tourKey="cashSetup" ready={!!data} onBeforeTour={() => setTab('entity')} />
        </div>
        <TestAsBar t={t} me={me} />

        {/* WHERE THE CHANGES ARE — the one band that says whether what is on screen is in force. */}
        <div className={`cs-band ${batch ? batch.status : 'clean'}`}>
          <span className={`cs-state ${batch ? batch.status : 'clean'}`}>
            {batch ? t(`cash.state_${batch.status}`) : t('cash.state_clean')}
          </span>
          <div className="cs-band-text">
            {!batch && (data.__empty ? t('cash.bandEmpty') : t('cash.bandClean'))}
            {batch && (
              <>
                <b className="mono">{batch.batch_no}</b>{' · '}
                {t('cash.bandCounts', { insert: batch.counts.insert, update: batch.counts.update, delete: batch.counts.delete })}
                {batch.status === 'submitted' && <> {' · '}{t('cash.sentBy', { by: batch.submitted_by, at: stamp(batch.submitted_at) })}</>}
                {batch.status === 'draft' && batch.returned_note && (
                  <div className="cs-returned">{t('cash.returned', { by: batch.decided_by, at: stamp(batch.decided_at) })} <b>{batch.returned_note}</b></div>
                )}
                {batch.status === 'submitted' && batch.submit_note && <div className="cs-returned">{batch.submit_note}</div>}
              </>
            )}
            {me.bootstrap && <div className="cs-boot">{me.portalAdmin ? t('cash.bootstrap') : t('cash.provisional', { roles: me.roles.join(', ') })}</div>}
          </div>
          <div className="cs-band-acts">
            {me.canEdit && !locked && (
              <label className={`pa-btn${busy ? ' disabled' : ''}`}>
                <Upload size={14} /> {t('cash.import')}
                <input type="file" accept=".xlsx" hidden disabled={busy} onChange={(e) => { onImport(e.target.files[0]); e.target.value = ''; }} />
              </label>
            )}
            {me.canEdit && batch?.status === 'draft' && batch.changes.length > 0 && (
              <>
                <input className="cs-note" value={sendNote} onChange={(e) => setSendNote(e.target.value)} placeholder={t('cash.notePh')} />
                <button type="button" className="pa-btn submit" disabled={busy}
                  title={counts.error ? t('cash.fixFirst') : undefined}
                  onClick={() => run(() => call(`${API}/setup/submit`, { method: 'POST', body: JSON.stringify({ note: sendNote }) }), t('cash.sent')).then((ok) => ok && setSendNote(''))}>
                  <Send size={14} /> {t('cash.send')}
                </button>
              </>
            )}
            {me.canApprove && locked && (
              <button type="button" className="pa-btn approve" disabled={busy} onClick={() => { setDecisionNote(''); setDeciding('approve'); }}>
                <CheckCircle2 size={14} /> {t('cash.approve')}
              </button>
            )}
          </div>
        </div>

        {msg && (
          <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`}>
            {msg.text}
            {msg.extra && <ul className="cs-blocking">{msg.extra.slice(0, 20).map((x) => <li key={x}>{x}</li>)}{msg.extra.length > 20 && <li>…</li>}</ul>}
          </div>
        )}

        <div className="cs-checkbar">
          <span className={`cs-sum ${counts.error ? 'error' : 'ok'}`}>
            {counts.error ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />} {t('cash.sumErrors', { count: counts.error })}
          </span>
          <span className="cs-sum warn">{t('cash.sumWarns', { count: counts.warn })}</span>
          <label><input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} /> {t('cash.onlyIssues')}</label>
          <label><input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} /> {t('cash.onlyChanged')}</label>
        </div>

        <div className="pa-tabs">
          {[...LIST_TABS, 'flow', 'changes', 'history', ...(me.allLevel && !me.bootstrap ? ['log'] : [])].map((l) => (
            <button key={l} type="button" className={`pa-tab ${tab === l ? 'active' : ''}`} onClick={() => setTab(l)}>
              {t(`cash.tab_${l}`)}
              {LIST_TABS.includes(l) && <span className="pa-count">{tabCount(l)}</span>}
              {LIST_TABS.includes(l) && issueCount(l) > 0 && <span className="cs-tab-err">{issueCount(l)}</span>}
              {l === 'changes' && batch && <span className="pa-count">{batch.changes.length}</span>}
            </button>
          ))}
        </div>

        {tab === 'period' && me.canEdit && !locked && (
          <div className="cs-yearbar">
            <span>{t('cash.addYear')}</span>
            <input type="number" value={newYear} onChange={(e) => setNewYear(Number(e.target.value))} />
            <button type="button" className="pa-btn" disabled={busy}
              onClick={() => run(async () => {
                const j = await call(`${API}/setup/periods/generate`, { method: 'POST', body: JSON.stringify({ year: newYear }) });
                say(t('cash.weeksAdded', { count: j.added, year: newYear }));
              })}>
              <Plus size={13} /> {t('cash.addWeeks')}
            </button>
          </div>
        )}

        {LIST_TABS.includes(tab) && tab !== 'fx' && (
          <ListTable key={tab} t={t} list={tab} rows={data[tab]} data={data} me={me} locked={locked} busy={busy}
            onSave={saveRow} onDelete={delRow} onUndo={undo} onlyIssues={onlyIssues} onlyChanged={onlyChanged} />
        )}
        {tab === 'fx' && <FxGrid t={t} data={data} me={me} locked={locked} busy={busy} onSaveMany={saveMany} onSave={saveRow} onSync={syncFx} />}
        {tab === 'flow' && <FlowTab t={t} />}
        {tab === 'changes' && <ChangesTab t={t} batch={batch} me={me} busy={busy} onUndo={undo} />}
        {tab === 'history' && <HistoryTab t={t} />}
        {tab === 'log' && <NoticeLog t={t} />}
      </div>

      {deciding && (
        <DecisionDialog
          spec={{ action: deciding, group: { groupId: batch.batch_no, groupName: t('cash.title') } }}
          note={decisionNote}
          onNote={setDecisionNote}
          busy={busy}
          selectedIds={[]}
          onSwitch={(a) => setDeciding(a)}
          onCancel={() => { setDeciding(null); setDecisionNote(''); }}
          onConfirm={async () => {
            const ok = await run(() => call(`${API}/setup/decide`, { method: 'POST', body: JSON.stringify({ action: deciding, note: decisionNote }) }),
              deciding === 'approve' ? t('cash.approved') : t('cash.rejected'));
            /* A refused decision closes the dialog too, or its overlay hides the banner saying why. */
            setDeciding(null);
            if (ok) setDecisionNote('');
          }}
        />
      )}
    </div>
  );
}
